import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  InvokeDataImportJobDiagnosisDocument,
  type InvokeDataImportJobDiagnosisMutation,
  type InvokeDataImportJobDiagnosisMutationVariables,
} from "~/features/dataImportProjects/gqls/mutation/invokeDataImportJobDiagnosis.generated";
import {
  GetDataImportJobDiagnosisDocument,
  type GetDataImportJobDiagnosisQuery,
  type GetDataImportJobDiagnosisQueryVariables,
} from "~/features/dataImportProjects/gqls/query/getDataImportJobDiagnosis.generated";
import {
  GetDataImportProjectConfigTimestampsDocument,
  type GetDataImportProjectConfigTimestampsQuery,
  type GetDataImportProjectConfigTimestampsQueryVariables,
} from "~/features/dataImportProjects/gqls/query/getDataImportProjectConfigTimestamps.generated";
import {
  ListDataImportJobDiagnosesDocument,
  type ListDataImportJobDiagnosesQuery,
  type ListDataImportJobDiagnosesQueryVariables,
} from "~/features/dataImportProjects/gqls/query/listDataImportJobDiagnoses.generated";
import { useTenant } from "~/features/tenant/context";

const POLLING_INTERVAL_MS = 3_000;
const POLLING_TIMEOUT_MS = 5 * 60 * 1_000;

export type DiagnosisStatus = "idle" | "invoking" | "polling" | "completed" | "failed";

export type DataImportJobDiagnosisResult = NonNullable<GetDataImportJobDiagnosisQuery["cnutGetDataImportJobDiagnosis"]>;

type QuickStatus = "not_run" | "ok" | "error" | "running";

export type DataImportJobDiagnosisHistoryItem =
  ListDataImportJobDiagnosesQuery["cnutListDataImportJobDiagnoses"][number];

type UseDataImportJobDiagnosisReturn = {
  invoke: () => Promise<void>;
  status: DiagnosisStatus;
  result: DataImportJobDiagnosisResult | null;
  diagnoses: ListDataImportJobDiagnosesQuery["cnutListDataImportJobDiagnoses"];
  fetchDiagnoses: () => void;
  latestDiagnosis: DataImportJobDiagnosisHistoryItem | null;
  quickStatus: QuickStatus;
  isStale: boolean;
};

function computeQuickStatus(
  status: DiagnosisStatus,
  latestDiagnosis: DataImportJobDiagnosisHistoryItem | null,
): QuickStatus {
  if (status === "invoking" || status === "polling") return "running";
  if (!latestDiagnosis) return "not_run";
  if (latestDiagnosis.status === "failed" || latestDiagnosis.results.some((r) => r.severity === "error")) {
    return "error";
  }
  return "ok";
}

function computeIsStale(
  latestDiagnosis: DataImportJobDiagnosisHistoryItem | null,
  configTimestamps: GetDataImportProjectConfigTimestampsQuery | null,
): boolean {
  if (!latestDiagnosis?.completedAt || !configTimestamps) return false;

  const completedAt = new Date(latestDiagnosis.completedAt).getTime();

  const timestamps: string[] = [];

  for (const dsip of configTimestamps.dataSourceImportProjects) {
    timestamps.push(dsip.dataSource.updatedAt);
  }
  for (const ei of configTimestamps.evidenceImporters) {
    timestamps.push(ei.updatedAt);
  }
  for (const rcd of configTimestamps.dataImportProjectRecordComposerDefinitions) {
    timestamps.push(rcd.recordComposerDefinition.updatedAt);
  }

  return timestamps.some((ts) => new Date(ts).getTime() > completedAt);
}

export function useDataImportJobDiagnosis(projectId: string): UseDataImportJobDiagnosisReturn {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [status, setStatus] = useState<DiagnosisStatus>("idle");
  const [result, setResult] = useState<DataImportJobDiagnosisResult | null>(null);
  const [diagnoses, setDiagnoses] = useState<ListDataImportJobDiagnosesQuery["cnutListDataImportJobDiagnoses"]>([]);
  const [configTimestamps, setConfigTimestamps] = useState<GetDataImportProjectConfigTimestampsQuery | null>(null);

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const diagnosisIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  const [invokeMutation] = useMutation<
    InvokeDataImportJobDiagnosisMutation,
    InvokeDataImportJobDiagnosisMutationVariables
  >(InvokeDataImportJobDiagnosisDocument);

  const [getDiagnosis] = useLazyQuery<GetDataImportJobDiagnosisQuery, GetDataImportJobDiagnosisQueryVariables>(
    GetDataImportJobDiagnosisDocument,
    {
      fetchPolicy: "network-only",
    },
  );

  const [listDiagnoses] = useLazyQuery<ListDataImportJobDiagnosesQuery, ListDataImportJobDiagnosesQueryVariables>(
    ListDataImportJobDiagnosesDocument,
    {
      fetchPolicy: "network-only",
    },
  );

  const [fetchConfigTimestamps] = useLazyQuery<
    GetDataImportProjectConfigTimestampsQuery,
    GetDataImportProjectConfigTimestampsQueryVariables
  >(GetDataImportProjectConfigTimestampsDocument, {
    fetchPolicy: "network-only",
  });

  const stopPolling = useCallback(() => {
    cancelledRef.current = true;
    if (pollingRef.current !== null) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fetchDiagnoses = useCallback(() => {
    if (!tenantId) return;

    listDiagnoses({
      variables: {
        input: {
          tenantId,
          dataImportProjectId: projectId,
          limit: 10,
        },
      },
    })
      .then(({ data }) => {
        if (data) {
          setDiagnoses(data.cnutListDataImportJobDiagnoses);
        }
      })
      .catch(() => {
        toast.error(t("feature.dataImportProjects.diagnosis.failed"));
      });
  }, [listDiagnoses, projectId, t, tenantId]);

  const pollDiagnosis = useCallback(() => {
    const currentDiagnosisId = diagnosisIdRef.current;
    if (!currentDiagnosisId || !tenantId) return;

    cancelledRef.current = false;

    const poll = () => {
      if (cancelledRef.current) return;

      getDiagnosis({
        variables: {
          input: {
            tenantId,
            projectId,
            diagnosisId: currentDiagnosisId,
          },
        },
      })
        .then(({ data }) => {
          if (cancelledRef.current) return;

          const diagnosis = data?.cnutGetDataImportJobDiagnosis;
          if (!diagnosis) {
            pollingRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
            return;
          }

          if (diagnosis.status === "completed") {
            stopPolling();
            setResult(diagnosis);
            setStatus("completed");
            toast.success(t("feature.dataImportProjects.diagnosis.completed"));
            fetchDiagnoses();
          } else if (diagnosis.status === "failed") {
            stopPolling();
            setResult(diagnosis);
            setStatus("failed");
            toast.error(diagnosis.errorMessage ?? t("feature.dataImportProjects.diagnosis.failed"));
            fetchDiagnoses();
          } else {
            pollingRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (cancelledRef.current) return;
          stopPolling();
          setStatus("failed");
          toast.error(t("feature.dataImportProjects.diagnosis.failed"));
        });
    };

    pollingRef.current = setTimeout(poll, POLLING_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setStatus("failed");
      toast.error(t("feature.dataImportProjects.diagnosis.timeout"));
    }, POLLING_TIMEOUT_MS);
  }, [fetchDiagnoses, getDiagnosis, projectId, stopPolling, t, tenantId]);

  const invoke = useCallback(async () => {
    if (!tenantId) return;

    cancelledRef.current = false;
    setStatus("invoking");
    setResult(null);

    try {
      const { data } = await invokeMutation({
        variables: {
          input: {
            tenantId,
            dataImportProjectId: projectId,
          },
        },
      });

      if (cancelledRef.current) return;

      const diagnosisId = data?.cnutInvokeDataImportJobDiagnosis.diagnosisId;
      if (!diagnosisId) {
        setStatus("failed");
        toast.error(t("feature.dataImportProjects.diagnosis.failed"));
        return;
      }

      diagnosisIdRef.current = diagnosisId;
      setStatus("polling");
      // Not a loading toast: polling reports its own outcome later, and nothing here would ever
      // dismiss a persistent one.
      toast.info(t("feature.dataImportProjects.diagnosis.invokeStarted"));
      pollDiagnosis();
    } catch (error: unknown) {
      if (cancelledRef.current) return;
      setStatus("failed");
      const message = error instanceof Error ? error.message : t("feature.dataImportProjects.diagnosis.failed");
      toast.error(message);
    }
  }, [invokeMutation, pollDiagnosis, projectId, t, tenantId]);

  // Auto-fetch latest diagnosis and config timestamps on mount
  // Note: state reset on projectId change is handled by keying the parent component
  useEffect(() => {
    if (!tenantId) return;

    listDiagnoses({
      variables: {
        input: {
          tenantId,
          dataImportProjectId: projectId,
          limit: 10,
        },
      },
    })
      .then(({ data }) => {
        if (data) {
          setDiagnoses(data.cnutListDataImportJobDiagnoses);
        }
      })
      .catch(() => {
        // Initial fetch failure is non-critical
      });

    fetchConfigTimestamps({
      variables: { tenantId, projectId },
    })
      .then(({ data }) => {
        if (data) {
          setConfigTimestamps(data);
        }
      })
      .catch(() => {
        // Config timestamps fetch failure is non-critical
      });
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const latestDiagnosis = useMemo<DataImportJobDiagnosisHistoryItem | null>(() => diagnoses[0] ?? null, [diagnoses]);

  const quickStatus = useMemo<QuickStatus>(
    () => computeQuickStatus(status, latestDiagnosis),
    [status, latestDiagnosis],
  );

  const isStale = useMemo<boolean>(
    () => computeIsStale(latestDiagnosis, configTimestamps),
    [latestDiagnosis, configTimestamps],
  );

  return {
    invoke,
    status,
    result,
    diagnoses,
    fetchDiagnoses,
    latestDiagnosis,
    quickStatus,
    isStale,
  };
}
